import { useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { buildLocalizedPath, type Lang } from "~/config/route-config";
import { getBrowserAuthClient } from "~/lib/auth-client";

/**
 * T872 S1 — the sign-out gesture as a hook, so the public cúter can offer «Cerrar
 * sesión» to a member with the same behaviour `AvatarMenu` has had since T98/T226:
 * the session stays valid on a network error and the error is surfaced for a retry,
 * never swallowed.
 */
export function useSignOut(onDone?: () => void) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);
  const inFlight = useRef(false);

  async function signOut() {
    if (inFlight.current) return;
    const client = getBrowserAuthClient();
    if (!client) return;
    inFlight.current = true;
    setSigningOut(true);
    try {
      // Better Auth's client does not throw on an HTTP error by default: it resolves with
      // `{ data: null, error }`. Treat that exactly like a rejection — the session is
      // still valid, so the menu stays and the retry is offered (PR #2032 review, P2).
      const result = (await client.signOut()) as { error?: unknown } | undefined;
      if (result && typeof result === "object" && "error" in result && result.error) {
        throw result.error;
      }
      const lang: Lang = i18n.language === "en" ? "en" : "es";
      setSignOutError(false);
      // T972 preview QA — the menu (and the ring + «Cerrando sesión…» in it) stays until
      // home has loaded; closing it on the POST left no wait on screen and focus on <body>.
      await navigate(buildLocalizedPath("home", lang));
      onDone?.();
    } catch {
      setSignOutError(true);
    } finally {
      inFlight.current = false;
      setSigningOut(false);
    }
  }

  return { signOut, signingOut, signOutError };
}
